import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ClipboardList, ScrollText, FileSignature, Stethoscope, Briefcase, Shield,
  Copy, Check, Save, UserPlus, FileDown, Download, Send, Link2, ExternalLink,
  Loader2, RefreshCw, Activity, Clock, ChevronDown, Globe, GitMerge, FlaskConical,
  MessageSquare, Users, ChevronLeft, ChevronRight
} from 'lucide-react';
import axios from 'axios';

import HourglassIcon from './HourglassIcon';
import NoteSignatureBlock from './NoteSignatureBlock';
import { AggregatePlanCompletenessChip } from './PlanCompletenessChip';
import { scorePlanCompleteness } from '../../utils/planCompleteness';
import EncounterStoplight from './EncounterStoplight';
import { detectIcd10Hints } from '../../utils/icd10HintEngine';
import { scanForDiagnoses, isHipaaRestrictive } from '../../utils/hipaaLint';
import { stripEmbeddedSignature } from '../../hooks/noteUtils';
import { AlertTriangle, ShieldCheck, ShieldAlert } from 'lucide-react';
import { FormatLockPill } from './FormatLockPill';
import { AddAnotherReferralBar } from './AddAnotherReferralBar';

// Count-up timer for generation overlay
const GenerationTimer = () => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(interval);
  }, []);
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return (
    <span className="text-xs text-muted-foreground tabular-nums font-mono">
      {mins > 0 ? `${mins}:${String(secs).padStart(2, '0')}` : `${secs}s`}
    </span>
  );
};

/**
 * TabsCarousel — wraps a wide TabsList in a horizontally-scrollable
 * container with left/right chevron buttons. Chevrons only appear when
 * overflow exists in that direction. Used when 7+ output tabs render
 * (e.g., multi-referral + multi-imaging + labs + script encounters).
 */
const TabsCarousel = ({ children, tabCount = 0 }) => {
  const scrollRef = React.useRef(null);
  const [canLeft, setCanLeft] = React.useState(false);
  const [canRight, setCanRight] = React.useState(true);

  const updateChevrons = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft;
    const max = el.scrollWidth - el.clientWidth;
    setCanLeft(left > 4);
    setCanRight(left < max - 4);
  }, []);

  React.useEffect(() => {
    updateChevrons();
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => updateChevrons();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateChevrons);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateChevrons);
    };
  }, [updateChevrons, tabCount]);

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const delta = Math.max(160, Math.round(el.clientWidth * 0.6));
    el.scrollBy({ left: dir * delta, behavior: 'smooth' });
  };

  return (
    <div className="relative" data-testid="output-tabs-carousel">
      {canLeft && (
        <button
          type="button"
          onClick={() => scrollBy(-1)}
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-9 w-7 flex items-center justify-center bg-card/95 hover:bg-card border border-border rounded-l-md shadow-md text-foreground/80 hover:text-primary transition-colors"
          aria-label="Scroll tabs left"
          data-testid="output-tabs-scroll-left"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}
      <div
        ref={scrollRef}
        className="overflow-x-auto scrollbar-none"
        style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingLeft: canLeft ? 28 : 0,
          paddingRight: canRight ? 28 : 0,
          transition: 'padding 0.2s',
        }}
      >
        {children}
      </div>
      {canRight && (
        <button
          type="button"
          onClick={() => scrollBy(1)}
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-9 w-7 flex items-center justify-center bg-card/95 hover:bg-card border border-border rounded-r-md shadow-md text-foreground/80 hover:text-primary transition-colors"
          aria-label="Scroll tabs right"
          data-testid="output-tabs-scroll-right"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
};

// Stable CopyButton component - defined outside the render to avoid re-mount issues
const CopyButtonLocal = ({ text, field, label = "Copy", handleCopy, copiedField, prominent = false }) => (
  <Button variant={prominent ? "secondary" : "ghost"} size="sm" onClick={() => handleCopy(text, field)}
    className={`${prominent ? 'h-9 px-4 bg-primary/15 hover:bg-primary/25 border border-primary/30 text-primary font-semibold shadow-sm' : 'h-8 px-3 text-muted-foreground hover:text-primary hover:bg-primary/10'} ${copiedField === field ? 'copy-success' : ''}`}
    data-testid={`copy-${field}-button`}
    title={`Copy ${label.toLowerCase()}`}>
    {copiedField === field ? <Check className="w-4 h-4 mr-1 text-green-600" /> : <Copy className="w-4 h-4 mr-1" />}
    {copiedField === field ? 'Copied!' : label}
  </Button>
);
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import GoldHourglass from './GoldHourglass';
import { toast } from 'sonner';

// Import shared components
import { LivePreview, EmptyState, ICD10CodesDisplay, WhatsNextAdvice } from './index';

// Feb 27, 2026 (v2) — Per-tab color identity. In high-contrast/dark mode the
// uniformly-dark TabsList made it hard to find Referrals vs Letters vs Imaging
// at a glance. Each tab now carries a soft tinted background even when
// inactive (so the eye can spot it) plus a saturated active state. Mapping
// is intentionally kept close to the in-app section iconography:
//   note     → emerald  (the primary clinical note)
//   referral → amber    (matches header Referrals chip)
//   imaging  → violet   (matches header Imaging chip)
//   labs     → sky      (matches header Labs chip)
//   letter   → yellow   (matches header Admin Letter chip)
//   portal   → cyan
//   script   → fuchsia  (visit-prep is the patient-facing voice)
//   limits   → teal     (matches header Profile / Limits chip)
// Mar 1, 2026 — Boosted contrast across the board per provider feedback:
//   • UNSELECTED tabs now use /25 background + 100% text-color (was /10 + /85
//     text which was hard to read against the dark surface).
//   • SELECTED tabs are now SOLID color (`bg-X-600`) with white text + a
//     2px border-2 accent so they pop clearly against the other tabs.
const TAB_COLOR = {
  note:     'bg-emerald-500/25  text-emerald-100  data-[state=active]:bg-emerald-600  data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-emerald-300 hover:bg-emerald-500/40',
  referral: 'bg-amber-500/25    text-amber-100    data-[state=active]:bg-amber-600    data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-amber-300   hover:bg-amber-500/40',
  imaging:  'bg-violet-500/25   text-violet-100   data-[state=active]:bg-violet-600   data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-violet-300  hover:bg-violet-500/40',
  labs:     'bg-sky-500/25      text-sky-100      data-[state=active]:bg-sky-600      data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-sky-300     hover:bg-sky-500/40',
  letter:   'bg-yellow-500/25   text-yellow-100   data-[state=active]:bg-yellow-600   data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-yellow-300  hover:bg-yellow-500/40',
  portal:   'bg-cyan-500/25     text-cyan-100     data-[state=active]:bg-cyan-600     data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-cyan-300    hover:bg-cyan-500/40',
  script:   'bg-fuchsia-500/25  text-fuchsia-100  data-[state=active]:bg-fuchsia-600  data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-fuchsia-300 hover:bg-fuchsia-500/40',
  limits:   'bg-teal-500/25     text-teal-100     data-[state=active]:bg-teal-600     data-[state=active]:text-white  data-[state=active]:border-2 data-[state=active]:border-teal-300    hover:bg-teal-500/40',
};
const tabCls = (key, extra = '') => `text-xs ${TAB_COLOR[key] || ''} ${extra}`.trim();


/**
 * OutputDocumentsPanel - Displays generated documents with tabs
 * 
 * Extracted from App.js to reduce monolithic component size.
 * Handles: Clinical Note, Referrals, Imaging Orders, Admin Letter, Limitations
 */
export const OutputDocumentsPanel = ({
  // Result data
  result,
  effectiveRole,
  
  // Tab state
  activeOutputTab,
  setActiveOutputTab,
  
  // Document state
  documentsReady,
  setDocumentsReady,
  generationVersion,
  autoSavedDraft,
  // Mar 2, 2026 — Clinical-safety pill. Provider entered these in the
  // form; we render them prominently next to the note title so any
  // LLM-induced age/gender drift is caught by eyeball inspection.
  patientAge,
  patientGender,
  
  // Actions
  saveDraft,
  copyAllDocuments,
  clearAll,
  generateDocuments,
  refreshAdminLetterOnly,
  refreshReferralOnly,
  scrubAndRefreshLetter,
  // Mar 2, 2026 — pass-through so the referral empty-state can toggle
  // referral generation and regenerate in one click. Same for Letter
  // and Limits empty-states.
  setShowReferralsSection,
  setGenerateAdminLetter,
  setGenerateLimitations,
  isGenerating,
  
  // Copy functionality
  copiedField,
  handleCopy,
  CopyButton,
  copyToClipboard,
  
  // Character limits
  charLimit,
  
  // Note title
  noteTitle,
  
  // Export functions
  exportToWord,
  exportToPDF,
  
  // Letter functions
  getFilledLetter,
  shareLetter,
  lastShareUrl,
  
  // ICD-10
  manualIcd10,
  primaryIcd10Codes,
  onReorderIcd10,
  onDeleteIcd10,
  liveIcd10Suggestions,
  setPrimaryIcd10Codes,
  autoDetectedCodes,
  
  // Billing codes
  getAdditionalBillingCodes,
  
  // Provider signature
  providerSignatureBlock,
  patientLabel,
  visitType,
  setVisitType,
  encounterType,
  virtualModality,
  setVirtualModality,
  dosOverride,
  setDosOverride,
  dosTimeOverride,
  setDosTimeOverride,
  // v6.8.48 — Late-entry flag passed to NoteSignatureBlock for the
  // "*** LATE ENTRY ***" stamp on the signed text.
  lateEntry,
  setLateEntry,
  
  // Team quick-share
  quickShareToTeam,
  autoShareTeamId,
  
  // Live preview data (for patient mode)
  notes,
  setNotes,
  soldierRank,
  soldierFirstName,
  soldierLastName,
  soldierDob,
  soldierUnit,
  letterType,
  occupation,
  jobDuties,
  patientStatus,
  
  // Layout
  forceSingleColumn,
  
  // ICD-10 Confirmation
  icd10PendingConfirm,
  confirmIcd10Sequence,
  dismissIcd10Confirm,
  // Result setter for translation
  setResult,
  // External setter — lets parent (App.js) capture the full signature block
  // (attestation + CPT + timestamps + sig) for the header Copy All button
  setExternalFullSignatureBlock,
  // History reconciliation — feeds the Encounter Stoplight's "Signed in
  // Genesis" toggle back to the server so the slide panel and main screen
  // stay in sync. Feb 16, 2026.
  loadHistory,
  updateHistoryItem,
}) => {
  // Store the full signature block text from NoteSignatureBlock for "Copy All" 
  const [fullSignatureBlock, setFullSignatureBlockLocal] = React.useState('');
  const setFullSignatureBlock = React.useCallback((val) => {
    setFullSignatureBlockLocal(val);
    if (setExternalFullSignatureBlock) setExternalFullSignatureBlock(val);
  }, [setExternalFullSignatureBlock]);
  // Spanish letter toggle state (used in admin_letter tab)
  const [showSpanishLetter, setShowSpanishLetter] = React.useState(false);
  // Translation loading state
  const [isTranslating, setIsTranslating] = React.useState(false);
  // Merge note state
  const [showMergeModal, setShowMergeModal] = React.useState(false);
  const [mergeText, setMergeText] = React.useState('');
  const [isMerging, setIsMerging] = React.useState(false);
  const [mergeStep, setMergeStep] = React.useState('paste'); // 'paste' or 'pick_codes'
  const [mergeIcd10List, setMergeIcd10List] = React.useState([]); // [{code, description, source, checked}]

  // Shared copy-all helper — invoked by both the existing "Copy All" button
  // AND the Encounter Stoplight's "Copy all (Note + Sig)" checkbox via the
  // `writeright:request-copy-all` event. Single source of truth so the
  // contents stay identical regardless of trigger surface.
  const triggerCopyAll = React.useCallback(() => {
    if (!result) return;
    const allContent = [];
    const icd10Codes = (liveIcd10Suggestions && liveIcd10Suggestions.length > 0 ? liveIcd10Suggestions : result.icd10_suggestions || []);
    if (icd10Codes.length > 0) allContent.push(`ICD-10 CODES: ${icd10Codes.map(c => typeof c === 'object' ? c.code : c).join(', ')}`);
    // Mar 1, 2026 — Include CPT/charge codes alongside ICD-10 so the unified
    // Copy All payload matches what the in-note "Copy All" button used to
    // emit pre-cleanup. Otherwise a one-handed paste loses billing context.
    const billingCodes = getAdditionalBillingCodes();
    if (billingCodes.length > 0) allContent.push(`CHARGE CODES: ${billingCodes.map(bc => `${bc.code} (${bc.description})`).join(', ')}`);
    if (result.referrals?.length > 0) result.referrals.forEach(r => allContent.push(`REFERRAL: ${r.specialty}\n${r.indication}`));
    else if (result.indication) allContent.push(`REFERRAL\n${result.indication}`);
    if (result.imaging_orders?.length > 0) result.imaging_orders.forEach(i => allContent.push(`IMAGING: ${i.imaging_type || i.type}${i.body_part ? ` - ${i.body_part}` : ''}\n${i.indication || i.content || ''}`));
    if (result.lab_orders) allContent.push(`LABS ORDERED:\n${result.lab_orders}`);
    if (result.clinical_note) allContent.push(`${noteTitle || 'CLINICAL NOTE'}\n${result.clinical_note}`);
    if (result.admin_letter) allContent.push(`ADMINISTRATIVE LETTER\n${result.admin_letter}`);
    if (result.portal_message) allContent.push(`PORTAL MESSAGE\n${result.portal_message}`);
    if (result.visit_prep_script) allContent.push(`VISIT PREP SCRIPT\n${result.visit_prep_script}`);
    if (result.limitations) allContent.push(`FUNCTIONAL LIMITATIONS\n${result.limitations}`);
    if (fullSignatureBlock) allContent.push(`---\n${fullSignatureBlock}`);
    handleCopy(allContent.join('\n\n'), 'all-docs');
  }, [result, liveIcd10Suggestions, fullSignatureBlock, noteTitle, handleCopy, getAdditionalBillingCodes]);

  // Bridge the stoplight checkbox to the shared copy-all helper
  React.useEffect(() => {
    const onCopyReq = () => triggerCopyAll();
    window.addEventListener('writeright:request-copy-all', onCopyReq);
    return () => window.removeEventListener('writeright:request-copy-all', onCopyReq);
  }, [triggerCopyAll]);

  // Extract ICD-10 codes from raw note text
  const extractIcd10FromText = (text) => {
    const pattern = /\b([A-Z]\d{2}(?:\.\d{1,4})?)\b/g;
    const found = new Set();
    let match;
    while ((match = pattern.exec(text)) !== null) {
      found.add(match[1]);
    }
    return [...found];
  };

  // Move to code selection step
  const handleProceedToCodes = () => {
    if (!mergeText.trim()) { toast.error('Paste your teammate\'s note first'); return; }
    
    // Get my confirmed codes
    const myCodes = (liveIcd10Suggestions || result.icd10_suggestions || []).map(c => ({
      code: typeof c === 'object' ? c.code : c,
      description: typeof c === 'object' ? (c.description || '') : '',
      source: 'mine',
      checked: true,
    }));
    const myCodeSet = new Set(myCodes.map(c => c.code));
    
    // Extract teammate's codes from their note text
    const teammateRawCodes = extractIcd10FromText(mergeText);
    const teammateCodes = teammateRawCodes
      .filter(code => !myCodeSet.has(code))
      .map(code => ({
        code,
        description: '',
        source: 'teammate',
        checked: true, // default checked — provider unchecks what they don't want
      }));
    
    setMergeIcd10List([...myCodes, ...teammateCodes]);
    setMergeStep('pick_codes');
  };

  const toggleMergeCode = (code) => {
    setMergeIcd10List(prev => prev.map(item => 
      item.code === code ? { ...item, checked: !item.checked } : item
    ));
  };

  // Handle note merge
  const handleMergeNote = async () => {
    const checkedCodes = mergeIcd10List.filter(c => c.checked).map(c => c.code).join(', ');
    setIsMerging(true);
    const toastId = toast.loading('Merging notes — deduplicating...');
    try {
      const response = await fetch(`${window.__WR_API_BASE__}/api/merge-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          my_note: result.clinical_note,
          teammate_note: mergeText.trim(),
          merge_mode: 'smart',
          confirmed_icd10: checkedCodes
        })
      });
      if (!response.ok) { const err = await response.json().catch(() => ({})); throw new Error(err.detail || 'Merge failed'); }
      const data = await response.json();
      if (data.merged_note && setResult) {
        setResult(prev => ({ ...prev, clinical_note: data.merged_note }));
        toast.success('Notes merged! Review the combined output.', { id: toastId });
        setShowMergeModal(false);
        setMergeText('');
        setMergeStep('paste');
        setMergeIcd10List([]);
      }
    } catch (e) {
      toast.error(`Merge failed: ${e.message}`, { id: toastId });
    } finally {
      setIsMerging(false);
    }
  };

  // Translate letter to Spanish
  const handleTranslateToSpanish = React.useCallback(async () => {
    if (!result?.admin_letter || isTranslating) return;
    setIsTranslating(true);
    try {
      const API = `${window.__WR_API_BASE__}/api`;
      const letterText = getFilledLetter ? getFilledLetter(result.admin_letter) : result.admin_letter;
      const res = await axios.post(`${API}/translate`, {
        text: letterText,
        target_language: 'es',
        document_type: 'admin_letter',
      }, { withCredentials: true });
      if (res.data.success && res.data.translated_text) {
        if (setResult) {
          setResult(prev => ({ ...prev, admin_letter_spanish: res.data.translated_text }));
        }
        setShowSpanishLetter(true);
        toast.success('Letter translated to Espa\u00f1ol!', { duration: 3000 });
      }
    } catch (e) {
      console.error('Translation failed:', e);
      toast.error('Translation failed. Please try again.');
    } finally {
      setIsTranslating(false);
    }
  }, [result, isTranslating, getFilledLetter, setResult]);

  // Feb 27, 2026 — Auto-scroll the output panel into view whenever a fresh
  // generation lands. Triggered by `generationVersion` (incremented in
  // useGeneration on every successful Generate/Regenerate). Without this,
  // 1-col layouts leave the output panel below the fold and the provider
  // doesn't realize the chart is ready; in 2-col it pulls the right column
  // back to the top so they see the new note immediately.
  const outputCardRef = React.useRef(null);
  // Snapshot the LLM's original clinical note per fresh generation so the
  // provider can revert inline edits with one click. Keyed by
  // generationVersion so each new generate stores a new baseline.
  const originalNoteRef = React.useRef('');
  const lastGenVersionRef = React.useRef(null);
  React.useEffect(() => {
    if (generationVersion !== lastGenVersionRef.current && result?.clinical_note) {
      originalNoteRef.current = result.clinical_note;
      lastGenVersionRef.current = generationVersion;
    }
  }, [generationVersion, result]);
  const isEdited = !!(result?.clinical_note
    && originalNoteRef.current
    && result.clinical_note.replace(/\*\*(.*?)\*\*/g, '$1') !== originalNoteRef.current.replace(/\*\*(.*?)\*\*/g, '$1'));
  React.useEffect(() => {
    if (!generationVersion) return;
    // Tiny delay so the panel has time to render its new content before
    // scrollIntoView measures its position. requestAnimationFrame is enough.
    const raf = requestAnimationFrame(() => {
      try {
        const el = outputCardRef.current;
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch { /* ignore */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [generationVersion]);
  
  return (
    <div id="output-section" className={`space-y-4 flex-1 flex flex-col ${!result && !isGenerating ? 'opacity-90' : ''}`} ref={outputCardRef}>
      {/* Your Documents - Main output card */}
      <Card 
        className={`border-border bg-card flex-1 ${
          !result && !isGenerating
            ? (forceSingleColumn ? 'min-h-[120px]' : 'min-h-[140px]')
            : (forceSingleColumn ? 'min-h-[500px]' : 'min-h-[400px]')
        } transition-all duration-500 relative ${
          documentsReady ? 'ring-4 ring-green-500 ring-offset-2 ring-offset-background border-green-500 shadow-lg shadow-green-500/30' : ''
        }`} 
        data-testid="output-card"
        onClick={() => documentsReady && setDocumentsReady(false)}
      >
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-foreground font-bold text-lg">
            <div className="flex items-center gap-2">
              <ClipboardList className={`w-5 h-5 ${documentsReady ? 'text-green-500 animate-pulse' : 'text-primary'}`} />
              <span className="elegant-title text-lg">Your Documents</span>
              {documentsReady && (
                <Badge className="bg-green-500 text-white font-bold animate-bounce ml-2">
                  ✓ READY v{generationVersion}
                </Badge>
              )}
              {/* Plan Completeness aggregate chip — patent Claim related to
                  real-time A/P feedback. Only shows once a note is generated. */}
              {result?.clinical_note && (() => {
                const codes = [
                  ...(result?.icd10_codes || []),
                  ...(autoDetectedCodes || []),
                  ...(manualIcd10 ? manualIcd10.split(/[,\s]+/).filter(Boolean) : []),
                ].filter(Boolean);
                const { aggregate } = scorePlanCompleteness(codes, result.clinical_note);
                return aggregate ? <AggregatePlanCompletenessChip aggregate={aggregate} /> : null;
              })()}
              {autoSavedDraft && result && !documentsReady && (
                <Badge className="bg-amber-500 text-white text-[10px] ml-2" title="Auto-saved draft">
                  ⏰ AUTO-DRAFT
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Create Docs / Save buttons at top of output */}
              {!result && !isGenerating && (
                <Button
                  onClick={generateDocuments}
                  disabled={isGenerating || !notes?.trim()}
                  size="sm"
                  className="h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                  data-testid="create-docs-output-top"
                  title={isGenerating ? "Generating..." : (notes?.trim() ? "Generate" : "Type or dictate notes first")}
                >
                  <img src="/emblem_icon.png" alt="" className="w-5 h-5 mr-1.5 object-contain inline-block" />Generate
                </Button>
              )}
              {result && (
                <Button
                  onClick={generateDocuments}
                  disabled={isGenerating || !notes?.trim()}
                  size="sm"
                  className="h-8 text-xs bg-primary hover:bg-primary/90 text-primary-foreground font-bold"
                  data-testid="create-docs-output-regen"
                  title={isGenerating ? "Generating..." : (notes?.trim() ? "Regenerate" : "Type or dictate notes first")}
                >
                  {isGenerating ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <img src="/emblem_icon.png" alt="" className="w-5 h-5 mr-1.5 object-contain inline-block" />}
                  {isGenerating ? 'Generating...' : 'Regenerate'}
                </Button>
              )}
              {/* Feb 28, 2026 — Format-Lock pill. Only meaningful when a
                  prior result exists (regen path); rendered alongside
                  Regenerate so the user sets the lock right before
                  clicking regenerate. */}
              {result && <FormatLockPill disabled={isGenerating} />}
              {result && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => saveDraft()} 
                  className="h-8 text-xs border-green-500/50 text-green-500 hover:bg-green-500/10" 
                  data-testid="save-output-btn"
                  title="Save to History & Share with Team"
                >
                  <Save className="w-3.5 h-3.5 mr-1.5" />Save
                </Button>
              )}
              {result && <Button variant="outline" size="sm" onClick={() => {
                triggerCopyAll();
              }} className="h-8 text-xs" data-testid="copy-all-docs-btn">
                {copiedField === 'all-docs' ? <Check className="w-3.5 h-3.5 mr-1.5 text-green-500" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}Copy All
              </Button>}
              {result && (
                <Button 
                  onClick={async () => {
                    await saveDraft();
                    clearAll(true);
                  }} 
                  variant="outline"
                  size="sm"
                  className="relative h-8 px-2.5 gap-1 text-xs font-bold border-2 border-emerald-400/70 text-emerald-200 bg-gradient-to-br from-emerald-500/30 via-emerald-500/20 to-emerald-600/30 hover:from-emerald-400/40 hover:via-emerald-400/30 hover:to-emerald-500/40 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/30 hover:scale-105 active:scale-95 transition-all shadow-sm shadow-emerald-500/20"
                  data-testid="next-pt-top-output"
                  title={effectiveRole === 'patient' ? 'Save & New Appt' : 'Save & Next Patient'}
                >
                  <Save className="w-3.5 h-3.5" />
                  <span className="text-[10px] opacity-70">+</span>
                  <UserPlus className="w-3.5 h-3.5" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-emerald-300 border border-emerald-100/80 shadow shadow-emerald-400/60 animate-pulse" aria-hidden="true" />
                </Button>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Loading overlay — ANIMATED GOLD HOURGLASS, positioned near top so it's
              always visible without scrolling on long output panels */}
          {isGenerating && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm z-10 flex items-start justify-center rounded-lg pt-16 sm:pt-20" data-testid="generating-overlay">
              <div className="flex flex-col items-center gap-4 sticky top-16">
                <GoldHourglass size={200} />
                <span className="text-lg font-bold text-center" style={{ color: '#daa520', textShadow: '0 0 12px rgba(218,165,32,0.3)' }}>
                  {result ? `Refining v${generationVersion + 1}...` : ['People First — we\'ll handle the paperwork...', 'People First. Paperwork in progress...'][Math.floor(Date.now() / 1000) % 2]}
                </span>
                <GenerationTimer />
              </div>
            </div>
          )}
          {result ? (
            <div className="space-y-4">
              {/* ICD-10 Codes at TOP - before output tabs */}
              {effectiveRole === 'provider' && ((result.icd10_suggestions && result.icd10_suggestions.length > 0) || manualIcd10) && (
                <ICD10CodesDisplay
                  icd10Suggestions={result.icd10_suggestions}
                  manualIcd10={manualIcd10}
                  primaryIcd10Codes={primaryIcd10Codes}
                  copiedField={copiedField}
                  onCopy={handleCopy}
                  onReorder={onReorderIcd10}
                  onDelete={onDeleteIcd10}
                  liveIcd10Suggestions={liveIcd10Suggestions}
                  setPrimaryIcd10Codes={setPrimaryIcd10Codes}
                />
              )}
              
              {/* "Did you mean?" hint pills — symptom→code mismatch heuristic
                  in the main provider flow. Feb 16, 2026 — extends the same
                  engine used in Speed Mode. Catches LLM picking E28.2 (PCOS)
                  when notes mention "dysmenorrhea" (should be N94.6).
                  Feb 26, 2026 — each row is now a CLICKABLE button. One tap:
                  • wrong_code → swap currentCode → suggestedCode in primaryIcd10Codes
                  • missing    → append suggestedCode to primaryIcd10Codes
                  No more exit-edit-resubmit dance. */}
              {effectiveRole === 'provider' && result && !isGenerating && (() => {
                const liveOrSuggested = (liveIcd10Suggestions && liveIcd10Suggestions.length > 0)
                  ? liveIcd10Suggestions
                  : (result.icd10_suggestions || []);
                const hints = detectIcd10Hints(notes || result.original_notes || '', liveOrSuggested);
                if (hints.length === 0) return null;

                const applyHint = (h) => {
                  if (!setPrimaryIcd10Codes) return;
                  setPrimaryIcd10Codes((prev) => {
                    const list = Array.isArray(prev) ? [...prev] : [];
                    if (h.type === 'wrong_code') {
                      const idx = list.findIndex((c) => {
                        const code = typeof c === 'string' ? c : c?.code;
                        return code === h.currentCode;
                      });
                      if (idx >= 0) {
                        // Preserve object shape if it was an object
                        list[idx] = typeof list[idx] === 'object'
                          ? { ...list[idx], code: h.suggestedCode, description: h.suggestedDesc }
                          : h.suggestedCode;
                      } else {
                        list.push(h.suggestedCode);
                      }
                    } else {
                      // missing — append if not already there
                      const exists = list.some((c) => (typeof c === 'string' ? c : c?.code) === h.suggestedCode);
                      if (!exists) list.push(h.suggestedCode);
                    }
                    return list;
                  });
                  // Trigger A/P regenerate so the new sequence takes effect
                  if (confirmIcd10Sequence) {
                    setTimeout(() => confirmIcd10Sequence(), 50);
                  }
                };

                return (
                  <div
                    className="rounded-lg border border-blue-500/30 bg-blue-500/5 px-3 py-2 space-y-1.5"
                    data-testid="icd10-hints-main"
                  >
                    <div className="text-xs font-bold text-blue-400 flex items-center gap-1.5">
                      <AlertTriangle className="w-3 h-3" />
                      Did you mean? ({hints.length}) <span className="text-[10px] text-blue-300/70 font-normal italic ml-1">— tap to apply</span>
                    </div>
                    <div className="space-y-1">
                      {hints.map((h, hi) => (
                        <button
                          type="button"
                          key={`${h.suggestedCode}-${hi}`}
                          onClick={() => applyHint(h)}
                          className="w-full flex items-center gap-2 text-xs px-2 py-1.5 rounded-md border border-transparent hover:border-blue-500/40 hover:bg-blue-500/10 transition-colors text-left group"
                          data-testid={`icd10-hint-apply-${h.suggestedCode}`}
                          title={h.type === 'wrong_code'
                            ? `Swap ${h.currentCode} → ${h.suggestedCode}`
                            : `Add missing code ${h.suggestedCode}`}
                        >
                          {h.type === 'wrong_code' ? (
                            <>
                              <span className="text-muted-foreground line-through font-mono text-[11px]">{h.currentCode}</span>
                              <span className="text-blue-400/60">→</span>
                              <span className="text-blue-300 font-mono font-semibold">{h.suggestedCode}</span>
                              <span className="text-blue-300/80 truncate flex-1">({h.suggestedDesc})</span>
                              <span className="text-[10px] text-muted-foreground italic shrink-0">notes mention "{h.matchedSymptom}"</span>
                            </>
                          ) : (
                            <>
                              <span className="text-muted-foreground italic text-[11px]">missing:</span>
                              <span className="text-blue-300 font-mono font-semibold">{h.suggestedCode}</span>
                              <span className="text-blue-300/80 truncate flex-1">({h.suggestedDesc})</span>
                              <span className="text-[10px] text-muted-foreground italic shrink-0">notes mention "{h.matchedSymptom}"</span>
                            </>
                          )}
                          <Check className="w-3 h-3 text-blue-400/40 group-hover:text-blue-300 shrink-0 transition-colors" />
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-muted-foreground/80 italic pt-0.5">
                      Tap a row to apply. Verify against the patient's actual presentation.
                    </p>
                  </div>
                );
              })()}
              
              {/* ICD-10 Confirmation Prompt — directly below drag-and-drop */}
              {icd10PendingConfirm && effectiveRole === 'provider' && (
                <div className="rounded-lg border-2 border-amber-500/40 bg-amber-500/5 p-2.5 flex items-center justify-between gap-3" data-testid="icd10-confirm-prompt">
                  <p className="text-xs font-semibold text-amber-400">Is this your final ICD-10 sequence?</p>
                  <div className="flex gap-2 shrink-0">
                    <Button 
                      size="sm" 
                      onClick={confirmIcd10Sequence}
                      className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-black font-bold"
                      data-testid="icd10-confirm-btn"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Confirm &amp; Regenerate A/P
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={dismissIcd10Confirm}
                      className="h-7 text-xs"
                      data-testid="icd10-dismiss-btn"
                    >
                      Keep as-is
                    </Button>
                  </div>
                </div>
              )}

              {/* Always-visible Reorder A/P button — regenerates A/P in current ICD-10 sequence */}
              {!icd10PendingConfirm && effectiveRole === 'provider' && result && !isGenerating && (
                <div className="flex justify-end">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={confirmIcd10Sequence}
                    className="h-7 text-xs border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:border-amber-500/50"
                    data-testid="reorder-ap-btn"
                  >
                    <RefreshCw className="w-3 h-3 mr-1.5" />
                    Refresh A/P by ICD-10 Order
                  </Button>
                </div>
              )}

              {/* Encounter Stoplight — 4-state status (Red/Yellow/Amber/Green) +
                  Copy All + Signed in Genesis checkboxes. Replaces having to
                  open the History bar to see what's done in real time. */}
              {effectiveRole === 'provider' && result && !isGenerating && (
                <EncounterStoplight
                  /* Stable encounter id. Feb 16, 2026 — the previous
                   * `enc-${Date.now()}` fallback regenerated on every render,
                   * so the stoplight could never share state with the History
                   * slide panel (which uses the real history_id). Now we
                   * always prefer the server's history_id and only fall back
                   * to a deterministic id derived from the result content. */
                  encounterId={result.history_id || result.id || `enc-${(result.clinical_note || '').length}-${(result.original_notes || '').length}`}
                  hasResult={!!result.clinical_note}
                  notesLength={(result.clinical_note || '').length}
                  onSignedToggle={async (checked) => {
                    // Sync the backend so the slide panel's checkbox state
                    // and the main-screen stoplight stay reconciled. Reported
                    // Feb 16, 2026 — checking "Signed in Genesis" on the main
                    // screen wasn't propagating to the slide panel checkbox.
                    const hid = result.history_id || result.id;
                    if (!hid || typeof hid !== 'string' || hid.startsWith('enc-')) return;
                    try {
                      if (updateHistoryItem) {
                        await updateHistoryItem(hid, { completed: !!checked }, { silent: true });
                      }
                      if (loadHistory) loadHistory();
                    } catch (e) {
                      console.warn('[EncounterStoplight] backend sync failed:', e?.message);
                    }
                  }}
                />
              )}
              
              <Tabs value={activeOutputTab} onValueChange={setActiveOutputTab}>
                {(() => {
                  // Count the ACTUAL tabs that will render so the CSS grid
                  // template has the right column count. Previously this was
                  // a partial heuristic (imaging + 4 / referrals + 3) that
                  // didn't include Labs / Portal / Script — when those
                  // rendered, the extra column (usually Limits) overflowed
                  // and got visually clipped / overlapped by the note title
                  // strip below. Bug reported May 6, 2026.
                  let _tabCount = 0;
                  if (effectiveRole === 'provider') _tabCount += 1; // Note
                  if (effectiveRole === 'provider') {
                    if (result.referrals && result.referrals.length > 1) {
                      _tabCount += result.referrals.length;
                    } else {
                      _tabCount += 1; // single Referral / Indication tab
                    }
                  }
                  if (effectiveRole === 'provider' && result.imaging_orders && result.imaging_orders.length > 0) {
                    _tabCount += result.imaging_orders.length;
                  }
                  if (effectiveRole === 'provider' && result.lab_orders) _tabCount += 1; // Labs
                  _tabCount += 1; // Letter (always)
                  if (result.portal_message) _tabCount += 1;
                  if (result.visit_prep_script) _tabCount += 1;
                  if (effectiveRole === 'provider') _tabCount += 1; // Limits
                  // 7+ tabs → render as horizontal carousel with chevrons,
                  // otherwise stick with a fixed grid so each tab gets
                  // equal width on common 4-6 tab encounters.
                  const _useCarousel = _tabCount >= 7;
                  const _cols = Math.max(1, Math.min(_tabCount, 10));
                  return _useCarousel ? (
                    <TabsCarousel tabCount={_tabCount}>
                      <TabsList className="inline-flex w-max bg-transparent h-9 gap-0.5">
                        {/* Note FIRST - Provider Only */}
                        {effectiveRole === 'provider' && (
                        <TabsTrigger value="clinical_note" disabled={!result.clinical_note} className={tabCls('note','px-3 flex-shrink-0')} data-testid="output-tab-note"><ScrollText className="w-3.5 h-3.5 mr-1" />Note</TabsTrigger>
                        )}
                        {/* Then Referrals - Provider Only */}
                        {effectiveRole === 'provider' && (result.referrals && result.referrals.length > 1 ? (
                          result.referrals.map((ref, index) => (
                            <TabsTrigger key={`referral-${index}`} value={`referral-${index}`} className={tabCls('referral','px-3 flex-shrink-0')} data-testid={`output-tab-referral-${index}`}>
                              <FileSignature className="w-3.5 h-3.5 mr-1" /><span className="truncate max-w-[80px]" title={ref.specialty}>{(ref.specialty || 'Referral').split(' ')[0].substring(0, 10)}</span>
                            </TabsTrigger>
                          ))
                        ) : (
                          <TabsTrigger value="indication" disabled={!result.indication && !(result.referrals && result.referrals.length)} className={tabCls('referral','px-3 flex-shrink-0')} data-testid="output-tab-indication">
                            <FileSignature className="w-3.5 h-3.5 mr-1" />Referral
                          </TabsTrigger>
                        ))}
                        {/* Then Imaging Orders - Provider Only */}
                        {effectiveRole === 'provider' && result.imaging_orders && result.imaging_orders.length > 0 && result.imaging_orders.map((img, index) => (
                          <TabsTrigger key={`imaging-${index}`} value={`imaging-${index}`} className={tabCls('imaging','px-3 flex-shrink-0')} data-testid={`output-tab-imaging-${index}`}>
                            <Stethoscope className="w-3.5 h-3.5 mr-1" /><span className="truncate max-w-[90px]" title={`${img.imaging_type || ''}${img.body_part ? ` - ${img.body_part}` : ''}`}>{img.body_part ? `${(img.imaging_type || '').substring(0, 3)} ${(img.body_part || '').split(' ')[0].substring(0, 6)}` : (img.imaging_type || 'Imaging').split(' ')[0].substring(0, 8)}</span>
                          </TabsTrigger>
                        ))}
                        {/* Labs Tab - Provider Only */}
                        {effectiveRole === 'provider' && result.lab_orders && (
                          <TabsTrigger value="labs" className={tabCls('labs','px-3 flex-shrink-0')} data-testid="output-tab-labs">
                            <FlaskConical className="w-3.5 h-3.5 mr-1" />Labs
                          </TabsTrigger>
                        )}
                        {/* Letter Tab - Always shown */}
                        <TabsTrigger value="admin_letter" disabled={!result.admin_letter} className={tabCls('letter','px-3 flex-shrink-0')} data-testid="output-tab-letter"><Briefcase className="w-3.5 h-3.5 mr-1" />Letter</TabsTrigger>
                        {/* Portal Message Tab */}
                        {result.portal_message && (
                          <TabsTrigger value="portal_message" className={tabCls('portal','px-3 flex-shrink-0')} data-testid="output-tab-portal-message"><MessageSquare className="w-3.5 h-3.5 mr-1" />Message</TabsTrigger>
                        )}
                        {/* Visit Prep Script Tab */}
                        {result.visit_prep_script && (
                          <TabsTrigger value="visit_prep_script" className={tabCls('script','px-3 flex-shrink-0')} data-testid="output-tab-visit-prep-script"><MessageSquare className="w-3.5 h-3.5 mr-1" />Script</TabsTrigger>
                        )}
                        {/* Limits Tab - Provider Only */}
                        {effectiveRole === 'provider' && (
                        <TabsTrigger value="limitations" disabled={!result.limitations} className={tabCls('limits','px-3 flex-shrink-0')} data-testid="output-tab-limitations"><Shield className="w-3.5 h-3.5 mr-1" />Limits</TabsTrigger>
                        )}
                      </TabsList>
                    </TabsCarousel>
                  ) : (
                    <TabsList className={`grid w-full bg-transparent h-9 gap-0.5`} style={{ gridTemplateColumns: `repeat(${_cols}, minmax(0, 1fr))` }}>
                      {/* Note FIRST - Provider Only */}
                      {effectiveRole === 'provider' && (
                      <TabsTrigger value="clinical_note" disabled={!result.clinical_note} className={tabCls('note')} data-testid="output-tab-note"><ScrollText className="w-3.5 h-3.5 mr-1" />Note</TabsTrigger>
                      )}
                      {/* Then Referrals - Provider Only */}
                      {effectiveRole === 'provider' && (result.referrals && result.referrals.length > 1 ? (
                        result.referrals.map((ref, index) => (
                          <TabsTrigger key={`referral-${index}`} value={`referral-${index}`} className={tabCls('referral')} data-testid={`output-tab-referral-${index}`}>
                            <FileSignature className="w-3.5 h-3.5 mr-1" /><span className="truncate max-w-[60px]" title={ref.specialty}>{(ref.specialty || 'Referral').split(' ')[0].substring(0, 8)}</span>
                          </TabsTrigger>
                        ))
                      ) : (
                        <TabsTrigger value="indication" disabled={!result.indication} className={tabCls('referral')} data-testid="output-tab-indication">
                          <FileSignature className="w-3.5 h-3.5 mr-1" />Referral
                        </TabsTrigger>
                      ))}
                      {/* Then Imaging Orders - Provider Only */}
                      {effectiveRole === 'provider' && result.imaging_orders && result.imaging_orders.length > 0 && result.imaging_orders.map((img, index) => (
                        <TabsTrigger key={`imaging-${index}`} value={`imaging-${index}`} className={tabCls('imaging')} data-testid={`output-tab-imaging-${index}`}>
                          <Stethoscope className="w-3.5 h-3.5 mr-1" /><span className="truncate max-w-[70px]" title={`${img.imaging_type || ''}${img.body_part ? ` - ${img.body_part}` : ''}`}>{img.body_part ? `${(img.imaging_type || '').substring(0, 3)} ${(img.body_part || '').split(' ')[0].substring(0, 4)}` : (img.imaging_type || 'Imaging').split(' ')[0].substring(0, 6)}</span>
                        </TabsTrigger>
                      ))}
                      {/* Labs Tab - Provider Only */}
                      {effectiveRole === 'provider' && result.lab_orders && (
                        <TabsTrigger value="labs" className={tabCls('labs')} data-testid="output-tab-labs">
                          <FlaskConical className="w-3.5 h-3.5 mr-1" />Labs
                        </TabsTrigger>
                      )}
                      {/* Letter Tab - Always shown */}
                      <TabsTrigger value="admin_letter" disabled={!result.admin_letter} className={tabCls('letter')} data-testid="output-tab-letter"><Briefcase className="w-3.5 h-3.5 mr-1" />Letter</TabsTrigger>
                      {/* Portal Message Tab */}
                      {result.portal_message && (
                        <TabsTrigger value="portal_message" className={tabCls('portal')} data-testid="output-tab-portal-message"><MessageSquare className="w-3.5 h-3.5 mr-1" />Message</TabsTrigger>
                      )}
                      {/* Visit Prep Script Tab */}
                      {result.visit_prep_script && (
                        <TabsTrigger value="visit_prep_script" className={tabCls('script')} data-testid="output-tab-visit-prep-script"><MessageSquare className="w-3.5 h-3.5 mr-1" />Script</TabsTrigger>
                      )}
                      {/* Limits Tab - Provider Only */}
                      {effectiveRole === 'provider' && (
                      <TabsTrigger value="limitations" disabled={!result.limitations} className={tabCls('limits')} data-testid="output-tab-limitations"><Shield className="w-3.5 h-3.5 mr-1" />Limits</TabsTrigger>
                      )}
                    </TabsList>
                  );
                })()}

                {/* Mar 2, 2026 — "+ Add Another Referral" bar with recent-
                    specialty chips (localStorage-backed) so providers don't
                    retype the same specialties every visit. Renders below
                    the multi-referral tabs when at least one referral
                    already exists. One tap appends without touching any
                    existing outputs (clinical note, admin letter, prior
                    referrals). */}
                {effectiveRole === 'provider' && result.referrals && result.referrals.length >= 1 && typeof refreshReferralOnly === 'function' && (
                  <AddAnotherReferralBar
                    isGenerating={isGenerating}
                    hasNotes={!!(notes && notes.trim())}
                    onAdd={(spec) => refreshReferralOnly([spec], { appendToExisting: true })}
                  />
                )}

                {/* Multiple Referral Tabs Content - Provider Only */}
                {effectiveRole === 'provider' && result.referrals && result.referrals.length > 1 && result.referrals.map((ref, index) => (
                  <TabsContent key={`referral-content-${index}`} value={`referral-${index}`} className="mt-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground uppercase tracking-wider">{ref.specialty} Referral</Label>
                          <Badge 
                            variant="outline" 
                            className={`text-xs font-mono ${
                              (ref.indication?.length || 0) > 290 
                                ? 'bg-red-500/20 border-red-500/50 text-red-400' 
                                : (ref.indication?.length || 0) > 260 
                                  ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                                  : 'bg-green-500/10 border-green-500/30 text-green-400'
                            }`}
                            title="Powerchart limit: 290 characters"
                          >
                            {ref.indication?.length || 0}/290
                          </Badge>
                        </div>
                        <CopyButtonLocal text={ref.indication} field={`referral-${index}`} handleCopy={handleCopy} copiedField={copiedField} />
                      </div>
                      <div
                        className="p-3 bg-muted rounded-lg border border-border text-sm text-foreground leading-relaxed whitespace-pre-wrap font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/40 cursor-text"
                        data-testid={`referral-output-${index}`}
                        contentEditable
                        suppressContentEditableWarning
                        spellCheck={true}
                        onBlur={(e) => {
                          const newText = e.currentTarget.textContent || '';
                          if (newText !== (ref.indication || '')) {
                            const updated = [...(result.referrals || [])];
                            updated[index] = { ...updated[index], indication: newText, char_count: newText.length };
                            setResult({ ...result, referrals: updated });
                          }
                        }}
                        title="Click to edit"
                      >{ref.indication}</div>
                      {(ref.indication?.length || 0) > 290 && (
                        <p className="text-xs text-red-400">⚠️ Over Powerchart limit (290 chars) - may need to shorten</p>
                      )}
                    </div>
                  </TabsContent>
                ))}

                {/* Imaging Order Tabs Content - Provider Only */}
                {effectiveRole === 'provider' && result.imaging_orders && result.imaging_orders.length > 0 && result.imaging_orders.map((img, index) => (
                  <TabsContent key={`imaging-content-${index}`} value={`imaging-${index}`} className="mt-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground uppercase tracking-wider">{img.imaging_type}{img.body_part ? ` - ${img.body_part}` : ''} Order</Label>
                          <Badge variant="outline" className="text-xs font-mono bg-blue-500/10 border-blue-500/30 text-blue-400">{img.char_count}/{charLimit} chars</Badge>
                        </div>
                        <CopyButtonLocal text={img.indication} field={`imaging-${index}`} handleCopy={handleCopy} copiedField={copiedField} />
                      </div>
                      <div
                        className="p-3 bg-muted rounded-lg border border-blue-500/30 text-sm text-foreground leading-relaxed whitespace-pre-wrap font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/40 cursor-text"
                        data-testid={`imaging-output-${index}`}
                        contentEditable
                        suppressContentEditableWarning
                        spellCheck={true}
                        onBlur={(e) => {
                          const newText = e.currentTarget.textContent || '';
                          if (newText !== (img.indication || '')) {
                            const updated = [...(result.imaging_orders || [])];
                            updated[index] = { ...updated[index], indication: newText, char_count: newText.length };
                            setResult({ ...result, imaging_orders: updated });
                          }
                        }}
                        title="Click to edit"
                      >{img.indication}</div>
                    </div>
                  </TabsContent>
                ))}

                {/* Single Indication Tab - Provider Only */}
                {effectiveRole === 'provider' && (
                <TabsContent value="indication" className="mt-3">
                  {result.indication && (!result.referrals || result.referrals.length <= 1) && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground uppercase tracking-wider">Referral Indication</Label>
                          {result.indication_char_count && <Badge variant="outline" className="text-xs font-mono bg-secondary border-border">{result.indication_char_count}/{charLimit} chars</Badge>}
                        </div>
                        <CopyButtonLocal text={result.indication} field="indication" handleCopy={handleCopy} copiedField={copiedField} />
                      </div>
                      <div
                        className="p-3 bg-muted rounded-lg border border-border text-sm text-foreground leading-relaxed whitespace-pre-wrap elegant-body min-h-[150px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500/40 cursor-text"
                        data-testid="indication-output"
                        contentEditable
                        suppressContentEditableWarning
                        spellCheck={true}
                        onBlur={(e) => {
                          const newText = e.currentTarget.textContent || '';
                          if (newText !== (result.indication || '')) {
                            setResult({ ...result, indication: newText, indication_char_count: newText.length });
                          }
                        }}
                        title="Click to edit"
                      >{result.indication}</div>
                    </div>
                  )}
                  {!result.indication && (!result.referrals || result.referrals.length === 0) && (
                    // Mar 2, 2026 — Provider was staring at a blank Referral
                    // tab after a Note-only regenerate. Explicit empty-state
                    // + one-click "+ Add Referral" button (toggles referral
                    // generation on and re-runs generate — no need to hunt
                    // for the toggle in Separate Documents).
                    <div className="p-4 text-xs text-muted-foreground text-center border border-border/40 border-dashed rounded space-y-2" data-testid="referral-empty-state">
                      <div className="italic">
                        No referral was generated for this note.
                      </div>
                      {typeof refreshReferralOnly === 'function' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            // Reveal the specialty picker so provider can see
                            // what's requested. If specialties are already
                            // populated, immediately fire referral-only regen
                            // (preserves the existing clinical note). If not,
                            // just reveal — provider will pick a specialty
                            // then click Add again.
                            if (typeof setShowReferralsSection === 'function') setShowReferralsSection(true);
                            const hasSpecs = Array.isArray(result?.referrals);
                            // We can't see the `specialties` state from here
                            // reliably, so just call the function — it
                            // validates and errors gracefully if empty.
                            setTimeout(() => refreshReferralOnly(), 100);
                          }}
                          disabled={isGenerating || !notes?.trim()}
                          className="h-8 text-xs border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
                          data-testid="add-referral-only-btn"
                          title="Add a referral without regenerating your existing note"
                        >
                          {isGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FileSignature className="w-3.5 h-3.5 mr-1.5" />}
                          Add Referral (keeps your note)
                        </Button>
                      )}
                      <div className="text-[10px] opacity-70">
                        Or toggle&nbsp;<span className="font-bold text-foreground">Referrals</span>&nbsp;in the &ldquo;Separate Documents&rdquo; row above.
                      </div>
                    </div>
                  )}
                </TabsContent>
                )}

                {/* Clinical Note Tab - Provider Only */}
                {effectiveRole === 'provider' && (
                <TabsContent value="clinical_note" className="mt-3" forceMount>
                  {result.clinical_note && (
                    <div className="space-y-3">
                      {/* Auto-generated Note Title - with Copy button */}
                      <div className="p-2 bg-primary/10 border border-primary/20 rounded-md">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="flex items-start gap-2 flex-1 min-w-[180px]">
                            <ScrollText className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                            <span className="text-sm font-semibold text-primary break-words" data-testid="note-title">{noteTitle}</span>
                            {/* Mar 2, 2026 — CLINICAL SAFETY pill. Shows the demographics
                                the provider ENTERED. Catches LLM slips where the note
                                header/body might reference wrong demographics (user
                                reported "2yo female" on a 68yo M note). Always visible
                                when demographics are set. */}
                            {(patientAge || patientGender) && (
                              <span
                                className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 whitespace-nowrap"
                                data-testid="verified-demographics-pill"
                                title="These are the demographics YOU entered — verify note matches"
                              >
                                ✓ {patientAge ? `${patientAge}yo` : ''}{patientAge && patientGender ? ' ' : ''}{
                                  patientGender === 'male' || patientGender === 'M' ? 'M'
                                  : patientGender === 'female' || patientGender === 'F' ? 'F'
                                  : patientGender || ''
                                }
                              </span>
                            )}
                            {isEdited && (
                              <span
                                className="ml-1 px-1.5 py-0.5 rounded text-[9px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                data-testid="note-edited-badge"
                                title="Note has been edited from the LLM original — your hand-edits will be copied"
                              >✓ EDITED</span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 flex-wrap shrink-0">
                            <CopyButtonLocal text={noteTitle} field="note_title" label="Title" handleCopy={handleCopy} copiedField={copiedField} />
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs"
                              onClick={triggerCopyAll}
                              title="Copy EVERYTHING — title + ICD + charge codes + referrals + imaging orders + lab orders + note + admin letter + signature block"
                              data-testid="copy-all-with-sig-btn"
                            >
                              {copiedField === 'all-docs' ? (
                                <><Check className="w-3 h-3 mr-1 text-green-500" />All</>
                              ) : (
                                <>Copy All</>
                              )}
                            </Button>
                            {/* Mar 1, 2026 — Removed the "All — no sig" button at the
                                top of the output panel. It was a near-duplicate of the
                                bottom-of-note "Note + Title" button (same payload sans
                                ICD/charge codes), and the provider asked to clean the
                                duplication. The bottom "Note + Title" button remains as
                                the primary daily action (title+note → one Genesis field). */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                              onClick={() => {
                                handleCopy(stripEmbeddedSignature(result.clinical_note), 'clinical_note_only_top');
                              }}
                              title="Copy ONLY the bare clinical note text — no title, no signature, no charge codes"
                              data-testid="copy-note-only-top-btn"
                            >
                              {copiedField === 'clinical_note_only_top' ? (
                                <><Check className="w-3 h-3 mr-1 text-green-500" />Note Only</>
                              ) : (
                                <>Note Only</>
                              )}
                            </Button>
                            {/* Sig Only — provider's primary daily action for the
                                separate Signature field in MHS Genesis. */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs border-sky-500/40 text-sky-300 hover:bg-sky-500/10"
                              onClick={() => {
                                handleCopy(fullSignatureBlock || '', 'sig_only_top');
                              }}
                              disabled={!fullSignatureBlock}
                              title={fullSignatureBlock
                                ? "Copy ONLY the signature block (attestation + CPT + timestamps + sig)"
                                : "Signature block not ready yet — scroll to set CPT / minutes first"}
                              data-testid="copy-sig-only-top-btn"
                            >
                              {copiedField === 'sig_only_top' ? (
                                <><Check className="w-3 h-3 mr-1 text-green-500" />Sig Only</>
                              ) : (
                                <>Sig Only</>
                              )}
                            </Button>
                            {/* Mar 1, 2026 — Orders Only: imaging + referrals + labs.
                                For providers who paste orders into the Genesis Orders
                                tab separately from the note. Skips title/note/sig. */}
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs border-purple-500/40 text-purple-300 hover:bg-purple-500/10"
                              onClick={() => {
                                const parts = [];
                                if (result.imaging_orders?.length > 0) {
                                  result.imaging_orders.forEach((i) => parts.push(
                                    `IMAGING: ${i.imaging_type || i.type}${i.body_part ? ` - ${i.body_part}` : ''}\n${i.indication || i.content || ''}`,
                                  ));
                                }
                                if (result.referrals?.length > 0) {
                                  result.referrals.forEach((r) => parts.push(
                                    `REFERRAL: ${r.specialty}\n${r.indication}`,
                                  ));
                                }
                                if (result.lab_orders) parts.push(`LABS ORDERED:\n${result.lab_orders}`);
                                if (parts.length === 0) {
                                  toast.info('No orders to copy (no imaging / referrals / labs generated this run)');
                                  return;
                                }
                                handleCopy(parts.join('\n\n'), 'orders_only_top');
                              }}
                              disabled={!result.imaging_orders?.length && !result.referrals?.length && !result.lab_orders}
                              title="Copy ONLY orders — imaging + referrals + labs (no note, no sig)"
                              data-testid="copy-orders-only-top-btn"
                            >
                              {copiedField === 'orders_only_top' ? (
                                <><Check className="w-3 h-3 mr-1 text-green-500" />Orders</>
                              ) : (
                                <>Orders Only</>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {/* Charge Code & ICD-10 Reminder Strip — top of note, uses user-reordered list */}
                      {((liveIcd10Suggestions && liveIcd10Suggestions.length > 0) || result.icd10_suggestions?.length > 0 || getAdditionalBillingCodes().length > 0) && (
                        <div className="flex flex-wrap items-center gap-2 px-3 py-2 rounded-lg border border-cyan-500/20 bg-cyan-500/5" data-testid="code-reminder-strip">
                          {((liveIcd10Suggestions && liveIcd10Suggestions.length > 0) || result.icd10_suggestions?.length > 0) && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">ICD-10:</span>
                              {(liveIcd10Suggestions && liveIcd10Suggestions.length > 0 ? liveIcd10Suggestions : result.icd10_suggestions).map((c, i) => (
                                <Badge key={i} variant="outline" className="text-[10px] font-mono border-cyan-500/40 text-cyan-300 bg-cyan-500/10" title={typeof c === 'object' ? c.description : ''}>{typeof c === 'object' ? c.code : c}</Badge>
                              ))}
                            </div>
                          )}
                          {getAdditionalBillingCodes().length > 0 && (
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="text-[10px] font-bold text-green-400 uppercase tracking-wider ml-1">Charge:</span>
                              {getAdditionalBillingCodes().map((bc, i) => (
                                <Badge 
                                  key={i} 
                                  variant="outline" 
                                  className="text-[10px] font-mono border-green-500/40 text-green-300 bg-green-500/10 cursor-help"
                                  title={bc.description}
                                  data-testid={`charge-code-top-${bc.code}`}
                                >
                                  {bc.code}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Quick Copy: Note + Title — the provider's PRIMARY daily action.
                            Title + Note goes into the Genesis A/P (one field); Sig goes
                            into the separate Genesis Signature field via "Sig Only" at
                            the top. The old "Note + Sig" button here was redundant with
                            the top-row "Copy All" — removed Mar 1, 2026 per provider
                            cleanup request. */}
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Clinical Note (SOAP)</Label>
                        <div className="flex items-center gap-1">
                          <Button
                            variant={copiedField === 'note_title_no_sig' ? 'default' : 'outline'}
                            size="sm"
                            className={`h-7 px-2 text-xs gap-1 border-amber-500/40 text-amber-300 hover:bg-amber-500/10 ${copiedField === 'note_title_no_sig' ? 'bg-green-600 text-white border-green-500' : ''}`}
                            onClick={() => {
                              const parts = [noteTitle, stripEmbeddedSignature(result.clinical_note)].filter(Boolean);
                              handleCopy(parts.join('\n\n'), 'note_title_no_sig');
                            }}
                            data-testid="copy-note-title-no-sig"
                            title="Copy Note + Title WITHOUT signature block"
                          >
                            {copiedField === 'note_title_no_sig' ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            {copiedField === 'note_title_no_sig' ? 'Copied!' : 'Note + Title'}
                          </Button>
                          {/* Removed the bottom-row "Note Only" copy button —
                              it was a duplicate of the same button at the top
                              of the output panel. Mar 1, 2026. */}
                          {quickShareToTeam && result.history_id && (
                            <Button variant="outline" size="sm"
                              onClick={() => quickShareToTeam(result.history_id)}
                              className="h-7 px-2 text-xs gap-1 border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                              data-testid="share-to-team-top"
                              title="Share to Team"
                            >
                              <Users className="w-3 h-3" />
                              Team
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => exportToWord(result.clinical_note, 'note', 'Clinical_Note')} className="h-8 px-2 text-muted-foreground hover:text-primary hover:bg-primary/10"><FileDown className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      
                      <div>
                        <div className="relative">
                        {isEdited && (
                          <button
                            type="button"
                            onClick={() => {
                              if (originalNoteRef.current) {
                                setResult({ ...result, clinical_note: originalNoteRef.current });
                                toast.success('Note reverted to LLM original', { duration: 1500 });
                              }
                            }}
                            className="absolute top-1 right-1 z-10 px-2 py-0.5 rounded-md border border-amber-500/50 bg-amber-500/15 text-amber-200 hover:bg-amber-500/25 text-[10px] font-semibold gap-1 inline-flex items-center transition-colors"
                            data-testid="reset-to-llm-original"
                            title="Revert your inline edits and restore the original LLM-generated note (Ctrl+Z works for the most recent edit too)"
                          >
                            ↺ Reset to LLM
                          </button>
                        )}
                        <div
                          // Mar 2, 2026 — CRITICAL FIX for the "blank note tab
                          // after regenerate" bug. React does NOT reliably
                          // rehydrate contentEditable children when state
                          // changes (React treats the DOM as user-controlled
                          // once contentEditable=true). Result: on regenerate,
                          // toast fired "Documents generated!" but the note
                          // area stayed empty because the div's textContent
                          // was never refreshed. Adding `key` tied to
                          // generationVersion forces React to unmount+remount
                          // on every regeneration, giving a fresh DOM node
                          // with the current clinical_note content.
                          key={`note-v${generationVersion || 0}`}
                          className="letter-output elegant-body min-h-[200px] resize-y focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-emerald-500/[0.02] rounded-md p-2 cursor-text"
                          data-testid="clinical-note-output"
                          contentEditable
                          suppressContentEditableWarning
                          spellCheck={true}
                          onBlur={(e) => {
                            const newText = e.currentTarget.textContent || '';
                            if (newText !== (result.clinical_note || '').replace(/\*\*(.*?)\*\*/g, '$1')) {
                              setResult({ ...result, clinical_note: newText });
                            }
                          }}
                          title="Click to edit directly — Ctrl+Z to undo your last keystroke, or use the Reset pill to revert all edits"
                        >{result.clinical_note.replace(/\*\*(.*?)\*\*/g, '$1')}</div>
                        </div>
                        <div className="flex items-center justify-end pt-2 gap-2 flex-wrap" data-testid="note-bottom-actions">
                          <Button
                            variant={copiedField === 'clinical_note_bottom' ? 'default' : 'default'}
                            size="sm"
                            className={`h-8 text-xs ${copiedField === 'clinical_note_bottom' ? 'bg-green-600 hover:bg-green-700' : 'bg-primary hover:bg-primary/90'}`}
                            onClick={() => {
                              const parts = [noteTitle, result.clinical_note, fullSignatureBlock].filter(Boolean);
                              handleCopy(parts.join('\n\n'), 'clinical_note_bottom');
                            }}
                            data-testid="copy-note-bottom-btn"
                            title="Copy Note + Title + full Signature Block"
                          >
                            {copiedField === 'clinical_note_bottom'
                              ? <Check className="w-3.5 h-3.5 mr-1.5" />
                              : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                            {copiedField === 'clinical_note_bottom' ? 'Copied!' : 'Note + Sig'}
                          </Button>
                          {quickShareToTeam && result.history_id && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => quickShareToTeam(result.history_id)}
                              className="h-8 text-xs border-blue-500/40 text-blue-400 hover:bg-blue-500/10"
                              data-testid="quick-share-team-btn"
                              title="Share to Team"
                            >
                              <Send className="w-3.5 h-3.5 mr-1.5" />
                              Team
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setShowMergeModal(true)}
                            className="h-8 text-xs border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                            data-testid="merge-note-btn"
                            title="Merge with another draft"
                          >
                            <GitMerge className="w-3.5 h-3.5 mr-1.5" />
                            Merge
                          </Button>
                        </div>
                      </div>
                      
                      {/* E/M Code Suggestion Badge - Below the note - Provider Only */}
                      {effectiveRole === 'provider' && result.em_code_suggestion && (
                        <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg" data-testid="em-code-suggestion">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Activity className="w-4 h-4 text-amber-500" />
                              <span className="text-sm font-bold text-amber-400">Coding Analysis</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className="bg-amber-500 text-black font-bold text-sm px-3">{result.em_code_suggestion.code}</Badge>
                              {result.em_code_suggestion.add_on && (
                                <Badge variant="outline" className="border-amber-500/50 text-amber-400 font-bold">{result.em_code_suggestion.add_on}</Badge>
                              )}
                            </div>
                          </div>
                          <p className="text-xs text-amber-200/80">{result.em_code_suggestion.description}</p>
                          <p className="text-xs text-amber-200/60 mt-1 italic">{result.em_code_suggestion.reasoning}</p>
                          
                          {/* LLM-detected Ancillary Charges */}
                          {result.em_code_suggestion.ancillary_codes && result.em_code_suggestion.ancillary_codes.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-amber-500/20">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold text-green-400">Ancillary Charges Detected:</span>
                              </div>
                              {result.em_code_suggestion.ancillary_codes.map((ac, idx) => (
                                <div key={idx} className="flex items-start gap-2 mb-1.5">
                                  <Badge variant="outline" className="border-green-500/50 text-green-400 shrink-0" data-testid={`ancillary-code-${ac.code}`}>
                                    {ac.code}
                                  </Badge>
                                  <div>
                                    <span className="text-xs text-green-300/80">{ac.description}</span>
                                    <p className="text-xs text-green-400/50 italic">{ac.justification}</p>
                                  </div>
                                </div>
                              ))}
                              <p className="text-xs text-green-400/50 mt-2">Your preparation, planning, and delivery—fully captured, fully credited.</p>
                            </div>
                          )}
                          
                          {/* Fallback: static ICD-10 based billing codes when no LLM ancillary */}
                          {(!result.em_code_suggestion.ancillary_codes || result.em_code_suggestion.ancillary_codes.length === 0) && getAdditionalBillingCodes().length > 0 && (
                            <div className="mt-3 pt-3 border-t border-amber-500/20">
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-semibold text-amber-400">Additional Billing Codes:</span>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {getAdditionalBillingCodes().map((bc, idx) => (
                                  <Badge 
                                    key={idx} 
                                    variant="outline" 
                                    className="border-green-500/50 text-green-400 cursor-pointer hover:bg-green-500/20 transition-colors"
                                    title={bc.description}
                                    data-testid={`billing-code-${bc.code}`}
                                  >
                                    {bc.code}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Note Signature Block */}
                      <NoteSignatureBlock
                        providerSignatureBlock={providerSignatureBlock}
                        patientLabel={patientLabel}
                        handleCopy={handleCopy}
                        copiedField={copiedField}
                        result={result}
                        noteTitle={noteTitle}
                        visitType={visitType}
                        setVisitType={setVisitType}
                        encounterType={encounterType}
                        getFilledLetter={getFilledLetter}
                        setResult={setResult}
                        onFullBlockChange={setFullSignatureBlock}
                        virtualModality={virtualModality}
                        setVirtualModality={setVirtualModality}
                        dosOverride={dosOverride}
                        setDosOverride={setDosOverride}
                        dosTimeOverride={dosTimeOverride}
                        setDosTimeOverride={setDosTimeOverride}
                        lateEntry={lateEntry}
                        setLateEntry={setLateEntry}
                      />
                    </div>
                  )}
                </TabsContent>
                )}

                {/* Admin Letter Tab */}

                {/* Labs Tab Content - Provider Only */}
                {effectiveRole === 'provider' && result.lab_orders && (
                <TabsContent value="labs" className="mt-3">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FlaskConical className="w-4 h-4 text-blue-400" />
                        <Label className="text-xs text-muted-foreground uppercase tracking-wider">Labs / Orders to Place</Label>
                      </div>
                      <CopyButtonLocal text={`LABS ORDERED:\n${result.lab_orders}`} field="labs" handleCopy={handleCopy} copiedField={copiedField} />
                    </div>
                    <div className="p-3 bg-muted rounded-lg border border-border text-sm text-foreground leading-relaxed whitespace-pre-wrap font-mono" data-testid="labs-output">{result.lab_orders}</div>
                    <p className="text-[10px] text-amber-400/70">Reminder: Place these orders in Genesis before closing the encounter.</p>
                  </div>
                </TabsContent>
                )}

                {/* Admin Letter Tab - actual content */}
                <TabsContent value="admin_letter" className="mt-3">
                  {!result.admin_letter && (
                    // Mar 2, 2026 — Empty-state + one-click "+ Add Letter"
                    // pattern, mirrors the Referral tab. Toggles the letter
                    // section on and regenerates so the provider doesn't
                    // hunt for the toggle in Separate Documents.
                    <div className="p-4 text-xs text-muted-foreground text-center border border-border/40 border-dashed rounded space-y-2" data-testid="letter-empty-state">
                      <div className="italic">
                        No admin letter was generated for this note.
                      </div>
                      {typeof setGenerateAdminLetter === 'function' && typeof generateDocuments === 'function' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setGenerateAdminLetter(true);
                            setTimeout(() => generateDocuments(), 50);
                          }}
                          disabled={isGenerating || !notes?.trim()}
                          className="h-8 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                          data-testid="add-letter-btn"
                          title="Enable Admin Letter generation and regenerate"
                        >
                          {isGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Briefcase className="w-3.5 h-3.5 mr-1.5" />}
                          Add Admin Letter (regenerate)
                        </Button>
                      )}
                      <div className="text-[10px] opacity-70">
                        Pick a letter type first, then click above.
                      </div>
                    </div>
                  )}
                  {result.admin_letter && (
                    <div className="space-y-3">
                      {/* Bilingual Toggle - shows when Spanish translation exists */}
                      {result.admin_letter_spanish && (
                        <div className="flex items-center justify-center gap-1 p-1 bg-muted/30 rounded-lg border border-border/50" data-testid="bilingual-toggle">
                          <Button
                            variant={!showSpanishLetter ? 'default' : 'ghost'}
                            size="sm"
                            className="h-7 px-4 text-xs flex-1"
                            onClick={() => setShowSpanishLetter(false)}
                            data-testid="letter-lang-en"
                          >
                            English (Provider)
                          </Button>
                          <Button
                            variant={showSpanishLetter ? 'default' : 'ghost'}
                            size="sm"
                            className="h-7 px-4 text-xs flex-1"
                            onClick={() => setShowSpanishLetter(true)}
                            data-testid="letter-lang-es"
                          >
                            Espa{'\u00f1'}ol (Paciente)
                          </Button>
                        </div>
                      )}
                      <div className="flex items-center justify-between flex-wrap gap-y-1">
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                            {showSpanishLetter && result.admin_letter_spanish ? 'Carta Administrativa (Espa\u00f1ol)' : 'Administrative Letter'}
                          </Label>
                          {/* HIPAA compliance chip — only renders for HIPAA-restrictive letter
                              types (FMLA, ADA, school 504, housing accommodation, etc.).
                              Green = scan found no diagnosis names or ICD-10 codes.
                              Amber = LLM may have leaked a diagnosis; provider should review. */}
                          {isHipaaRestrictive(letterType) && (() => {
                            const letterTextForScan = getFilledLetter(
                              showSpanishLetter && result.admin_letter_spanish
                                ? result.admin_letter_spanish
                                : result.admin_letter
                            );
                            const scan = scanForDiagnoses(letterTextForScan);
                            if (scan.ok) {
                              return (
                                <span
                                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/40"
                                  title="No diagnosis names or ICD-10 codes detected. Best-effort scan — provider review still required."
                                  data-testid="hipaa-ok-chip"
                                >
                                  <ShieldCheck className="w-3 h-3" />
                                  HIPAA OK
                                </span>
                              );
                            }
                            const found = [...scan.hits, ...scan.icd10Hits].slice(0, 4).join(', ');
                            const allFlagged = [...scan.hits, ...scan.icd10Hits];
                            const canScrub = typeof scrubAndRefreshLetter === 'function' && !isGenerating;
                            return (
                              <button
                                type="button"
                                onClick={canScrub ? () => scrubAndRefreshLetter(allFlagged) : undefined}
                                disabled={!canScrub}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/15 text-amber-400 border border-amber-500/40 ${canScrub ? 'cursor-pointer hover:bg-amber-500/25 hover:border-amber-400/70 transition-colors' : 'cursor-help opacity-75'}`}
                                title={canScrub
                                  ? `Possible diagnosis leak: ${found}${allFlagged.length > 4 ? '…' : ''}. Click to scrub & regenerate this letter without these terms.`
                                  : `Possible diagnosis leak: ${found}${allFlagged.length > 4 ? '…' : ''}. Review before signing.`}
                                data-testid="hipaa-review-chip"
                              >
                                <ShieldAlert className="w-3 h-3" />
                                {canScrub ? 'Scrub & Refresh' : 'Review'}: {allFlagged.length} flag{allFlagged.length === 1 ? '' : 's'}
                              </button>
                            );
                          })()}
                        </div>
                        <div className="flex items-center gap-1">
                          {refreshAdminLetterOnly && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={refreshAdminLetterOnly}
                              disabled={isGenerating}
                              className="h-8 px-2 text-muted-foreground hover:text-amber-400 hover:bg-amber-500/10 gap-1"
                              title="Regenerate just this letter (keeps your date and filled-in fields)"
                              data-testid="refresh-letter-only-btn"
                            >
                              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                              <span className="text-xs hidden sm:inline">Refresh Letter</span>
                            </Button>
                          )}
                          <CopyButtonLocal text={getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter)} field="admin_letter" handleCopy={handleCopy} copiedField={copiedField} />
                          <Button variant="ghost" size="sm" onClick={() => exportToWord(getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter), 'letter', 'Admin_Letter')} className="h-8 px-2 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Download Word"><FileDown className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => exportToPDF(getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter), 'letter', 'Admin_Letter')} className="h-8 px-2 text-muted-foreground hover:text-primary hover:bg-primary/10" title="Download PDF"><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => shareLetter(getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter))} className="h-8 px-2 text-muted-foreground hover:text-green-500 hover:bg-green-500/10" title="Share to Soldier via SMS"><Send className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div className="relative group">
                        {/* Feb 28, 2026 (v6.8.44) — Admin letter is now
                            inline-editable, matching the clinical note
                            UX (line ~1186). The user's friend wanted to
                            tweak a few words of her ESA letter before
                            downloading the .docx; before this fix she
                            had to wait 30 sec for a regenerate. Now she
                            just clicks into the letter and types.
                            The letter shows `getFilledLetter(...)` which
                            already interpolated placeholders — on blur
                            we write the FINAL text back to
                            `result.admin_letter`, which is what the
                            download/copy/share buttons read. */}
                        <div
                          key={`letter-v${generationVersion || 0}-${showSpanishLetter ? 'es' : 'en'}`}
                          className="letter-output elegant-body min-h-[200px] resize-y focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:bg-amber-500/[0.02] rounded-md p-2 cursor-text"
                          style={{resize: 'vertical'}}
                          data-testid="admin-letter-output"
                          contentEditable
                          suppressContentEditableWarning
                          spellCheck={true}
                          onBlur={(e) => {
                            const newText = e.currentTarget.innerText || '';
                            const sourceField = (showSpanishLetter && result.admin_letter_spanish) ? 'admin_letter_spanish' : 'admin_letter';
                            const filledCurrent = getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter);
                            if (newText !== filledCurrent) {
                              setResult({ ...result, [sourceField]: newText });
                            }
                          }}
                          title="Click to edit directly — Ctrl+Z to undo your last keystroke"
                        >
                          {getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter)}
                        </div>
                        <div className="flex justify-end pt-2">
                          <CopyButtonLocal text={getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter)} field="admin_letter_bottom" label="Copy Letter" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground text-right">↕ Drag corner to resize · click letter to edit inline before downloading</p>
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => exportToWord(getFilledLetter(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter), 'letter', `WriteRight_${letterType || 'Letter'}`)} className="flex-1 h-9 border-border text-foreground hover:bg-secondary"><FileDown className="w-4 h-4 mr-2" />Download Word (.docx)</Button>
                        <Button variant="outline" size="sm" onClick={() => exportToPDF(showSpanishLetter && result.admin_letter_spanish ? result.admin_letter_spanish : result.admin_letter, 'letter', `WriteRight_${letterType || 'Letter'}`)} className="flex-1 h-9 border-border text-foreground hover:bg-secondary"><Download className="w-4 h-4 mr-2" />Download PDF</Button>
                      </div>
                      {/* Translate to Spanish button — Provider Mode on-demand translation */}
                      {!result.admin_letter_spanish && effectiveRole === 'provider' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleTranslateToSpanish}
                          disabled={isTranslating}
                          className="w-full h-9 border-amber-500/40 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300 gap-2"
                          data-testid="translate-to-spanish-btn"
                        >
                          {isTranslating ? (
                            <><Loader2 className="w-4 h-4 animate-spin" />Translating...</>
                          ) : (
                            <><Globe className="w-4 h-4" />Translate to Espa{'\u00f1'}ol</>
                          )}
                        </Button>
                      )}
                      {lastShareUrl && (
                        <div className="flex items-center gap-2 pt-2 p-2 rounded-lg bg-green-500/10 border border-green-500/30">
                          <Link2 className="w-4 h-4 text-green-500 shrink-0" />
                          <span className="text-xs text-green-400 truncate flex-1" title={lastShareUrl}>{lastShareUrl}</span>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-green-500 hover:text-green-400 hover:bg-green-500/20" onClick={async () => { await copyToClipboard(lastShareUrl); toast.success('Share link copied!'); }} title="Copy share link" data-testid="copy-share-link-btn"><Copy className="w-3.5 h-3.5 mr-1" />Copy Link</Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-green-500 hover:text-green-400 hover:bg-green-500/20" onClick={() => window.open(lastShareUrl, '_blank')} title="Open share link"><ExternalLink className="w-3.5 h-3.5" /></Button>
                        </div>
                      )}
                      
                      {/* What's Next Advice Section */}
                      <WhatsNextAdvice />
                    </div>
                  )}
                </TabsContent>

                {/* Portal Message Tab */}
                {result.portal_message && (
                  <TabsContent value="portal_message" className="mt-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-blue-400 uppercase tracking-wider flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Portal Message
                        </Label>
                        <CopyButtonLocal text={result.portal_message} field="portal_message" label="Copy Message" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                      </div>
                      {/* v6.8.44 — Portal message is now inline-editable
                          (was read-only). Click to edit, blur to save. */}
                      <div className="p-4 bg-blue-500/5 border border-blue-500/20 rounded-lg space-y-2" data-testid="portal-message-output">
                        <div
                          key={`portal-v${generationVersion || 0}`}
                          className="whitespace-pre-wrap text-sm text-foreground leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:bg-blue-500/[0.02] rounded p-1 cursor-text"
                          contentEditable
                          suppressContentEditableWarning
                          spellCheck={true}
                          onBlur={(e) => {
                            const newText = e.currentTarget.innerText || '';
                            if (newText !== result.portal_message) {
                              setResult({ ...result, portal_message: newText });
                            }
                          }}
                          title="Click to edit before copying"
                        >
                          {result.portal_message}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Copy this message and paste it into your patient portal (MHS Genesis, MyChart, etc.) · click message to edit inline
                      </p>
                      <div className="flex justify-center pt-1">
                        <CopyButtonLocal text={result.portal_message} field="portal_message_bottom" label="Copy to Clipboard" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                      </div>
                    </div>
                  </TabsContent>
                )}

                {/* Visit Prep Script Tab — talking points for verbal advocacy */}
                {result.visit_prep_script && (
                  <TabsContent value="visit_prep_script" className="mt-3">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <Label className="text-xs text-emerald-400 uppercase tracking-wider flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Visit Prep Script
                        </Label>
                        <CopyButtonLocal text={result.visit_prep_script} field="visit_prep_script" label="Copy Script" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                      </div>
                      {/* v6.8.44 — Visit Prep Script is now inline-editable. */}
                      <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg" data-testid="visit-prep-script-output">
                        <div
                          className="whitespace-pre-wrap text-sm text-foreground leading-relaxed font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:bg-emerald-500/[0.02] rounded p-1 cursor-text"
                          contentEditable
                          suppressContentEditableWarning
                          spellCheck={true}
                          onBlur={(e) => {
                            const newText = e.currentTarget.innerText || '';
                            if (newText !== result.visit_prep_script) {
                              setResult({ ...result, visit_prep_script: newText });
                            }
                          }}
                          title="Click to edit before copying"
                        >
                          {result.visit_prep_script}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Read this while you wait. It's designed for verbal delivery — short sentences, protocol citations, and anticipated pushback responses. Not a letter — a script.
                      </p>
                      <div className="flex justify-center pt-1 gap-2 flex-wrap">
                        <CopyButtonLocal text={result.visit_prep_script} field="visit_prep_script_bottom" label="Copy to Clipboard" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // SMS-friendly: strip markdown bold/italic, strip header lines, keep dialogue only
                            const raw = result.visit_prep_script || '';
                            const smsText = raw
                              .replace(/\*\*(.*?)\*\*/g, '$1') // strip **bold**
                              .replace(/\*(.*?)\*/g, '$1')      // strip *italic*
                              .replace(/^#+\s*/gm, '')          // strip markdown headers
                              .replace(/^\s*[-•]\s*/gm, '- ')   // normalize bullets
                              .replace(/\n{3,}/g, '\n\n')       // collapse extra blank lines
                              .replace(/\s+$/gm, '')            // trim trailing spaces
                              .trim();
                            handleCopy(smsText, 'visit_prep_script_sms');
                          }}
                          className="h-8 px-3 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          data-testid="visit-prep-script-sms-btn"
                          title="Copy as plaintext SMS — send to provider/nursing when DOCX/PDF won't open on phone"
                        >
                          <MessageSquare className="w-3.5 h-3.5 mr-1" />
                          {copiedField === 'visit_prep_script_sms' ? 'Copied for SMS!' : 'Copy as SMS'}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => exportToWord(result.visit_prep_script, 'note', 'Visit_Prep_Script')}
                          className="h-8 px-3 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                          data-testid="visit-prep-script-docx-btn"
                        >
                          <FileDown className="w-3.5 h-3.5 mr-1" />
                          Print-friendly DOCX
                        </Button>
                      </div>
                    </div>
                  </TabsContent>
                )}

                {/* Functional Limitations Tab - Available to both */}
                <TabsContent value="limitations" className="mt-3">
                  {!result.limitations && (
                    // Mar 2, 2026 — Empty-state pattern for Limits tab.
                    <div className="p-4 text-xs text-muted-foreground text-center border border-border/40 border-dashed rounded space-y-2" data-testid="limits-empty-state">
                      <div className="italic">
                        No profile / duty limitations were generated for this note.
                      </div>
                      {typeof setGenerateLimitations === 'function' && typeof generateDocuments === 'function' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setGenerateLimitations(true);
                            setTimeout(() => generateDocuments(), 50);
                          }}
                          disabled={isGenerating || !notes?.trim()}
                          className="h-8 text-xs border-green-500/40 text-green-300 hover:bg-green-500/10"
                          data-testid="add-limits-btn"
                          title="Enable Profile / Duty Restrictions generation and regenerate"
                        >
                          {isGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Shield className="w-3.5 h-3.5 mr-1.5" />}
                          Add Limits (regenerate)
                        </Button>
                      )}
                    </div>
                  )}
                  {result.limitations && (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs text-green-500 uppercase tracking-wider flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          {effectiveRole === 'provider' ? 'Profile / Duty Restrictions (MEDPROS)' : 'Suggested Limitations for Your Letter'}
                        </Label>
                        <div className="flex items-center gap-1">
                          <CopyButtonLocal text={result.limitations} field="limitations" handleCopy={handleCopy} copiedField={copiedField} />
                          <Button variant="ghost" size="sm" onClick={() => exportToWord(result.limitations, 'note', 'Functional_Limitations')} className="h-8 px-2 text-muted-foreground hover:text-green-500 hover:bg-green-500/10" title="Download Word"><FileDown className="w-4 h-4" /></Button>
                        </div>
                      </div>
                      <div>
                        {/* v6.8.44 — Limitations text is now inline-editable. */}
                        <div className="limitations-output min-h-[200px] resize-y p-4 bg-green-500/5 border border-green-500/30 rounded-lg" data-testid="limitations-output">
                          <pre
                            className="text-sm text-foreground whitespace-pre-wrap font-sans leading-relaxed focus:outline-none focus:ring-2 focus:ring-green-500/40 focus:bg-green-500/[0.02] rounded p-1 cursor-text"
                            contentEditable
                            suppressContentEditableWarning
                            spellCheck={true}
                            onBlur={(e) => {
                              const newText = e.currentTarget.innerText || '';
                              if (newText !== result.limitations) {
                                setResult({ ...result, limitations: newText });
                              }
                            }}
                            title="Click to edit before copying"
                          >{result.limitations}</pre>
                        </div>
                        <div className="flex justify-end pt-2">
                          <CopyButtonLocal text={result.limitations} field="limitations_bottom" label="Copy" handleCopy={handleCopy} copiedField={copiedField} prominent={true} />
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {effectiveRole === 'provider' 
                          ? 'Copy this text directly into MEDPROS free text fields. Compliant with OTSG/MEDCOM Policy 21-019.'
                          : 'These are suggested limitations based on your condition and job duties. Include relevant ones in your letter.'}
                      </p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>

            </div>
          ) : (
            /* Live Preview for Patients OR Empty State */
            effectiveRole === 'patient' && (notes.trim() || soldierFirstName || letterType) ? (
              <LivePreview
                notes={notes}
                soldierRank={soldierRank}
                soldierFirstName={soldierFirstName}
                soldierLastName={soldierLastName}
                soldierDob={soldierDob}
                soldierUnit={soldierUnit}
                letterType={letterType}
                occupation={occupation}
                jobDuties={jobDuties}
              />
            ) : (
              <EmptyState effectiveRole={effectiveRole} />
            )
          )}
          
          {/* Bottom Action Buttons - Regenerate and Clear Patient */}
          {result && (
            <div className="flex gap-3 pt-4 mt-4 border-t border-border">
              <Button 
                onClick={generateDocuments} 
                disabled={isGenerating || !notes.trim()} 
                variant="outline"
                className="flex-1 h-10 border-primary/50 text-primary hover:bg-primary/10"
                data-testid="regenerate-button"
                title={isGenerating ? "Generating..." : (notes?.trim() ? "Regenerate Note" : "Type or dictate notes first")}
              >
                {isGenerating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</>
                ) : (
                  <><img src="/emblem_icon.png" alt="" className="w-5 h-5 mr-2 object-contain inline-block" />Regenerate Note</>
                )}
              </Button>
              {/* Copy Note Only — copies the bare clinical note text (no title,
                  no signature, no letter, no orders). Distinct from "Copy All"
                  (top button) and "Copy Note + Title + Sig" (inside Note tab). */}
              {result.clinical_note && effectiveRole === 'provider' && (
                <Button
                  onClick={() => handleCopy(result.clinical_note, 'clinical_note_only_bottom')}
                  variant="outline"
                  className={`h-10 px-4 border-emerald-500/50 text-emerald-500 hover:bg-emerald-500/10 font-medium gap-1.5 ${copiedField === 'clinical_note_only_bottom' ? 'bg-emerald-500/15' : ''}`}
                  data-testid="copy-note-only-bottom-btn"
                  title="Copy only the clinical note text — no title, no signature, no orders"
                >
                  {copiedField === 'clinical_note_only_bottom'
                    ? <><Check className="w-4 h-4" />Copied!</>
                    : <><Copy className="w-4 h-4" />Copy Note Only</>}
                </Button>
              )}
              <Button 
                onClick={async () => {
                  await saveDraft();
                  clearAll(true);
                }} 
                variant="default"
                className="relative h-10 px-5 gap-1.5 font-bold border-2 border-emerald-400/70 text-emerald-100 bg-gradient-to-br from-emerald-500/40 via-emerald-500/30 to-emerald-600/40 hover:from-emerald-400/50 hover:via-emerald-400/40 hover:to-emerald-500/50 hover:border-emerald-300 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-105 active:scale-95 transition-all shadow-md shadow-emerald-500/20"
                data-testid="clear-patient-bottom-button"
                title={effectiveRole === 'patient' ? 'Save & New Appt' : 'Save & Next Patient'}
              >
                <Save className="w-4 h-4" />
                <span className="mx-0.5 opacity-70">+</span>
                <UserPlus className="w-4 h-4" />
                <span className="text-sm">{effectiveRole === 'patient' ? 'New Appt' : 'Next Patient'}</span>
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-emerald-300 border border-emerald-100/80 shadow shadow-emerald-400/60 animate-pulse" aria-hidden="true" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Merge Note Modal ─── */}
      {showMergeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-testid="merge-note-modal">
          <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <GitMerge className="w-5 h-5 text-amber-500" />
                <h3 className="font-semibold text-base">
                  {mergeStep === 'paste' ? 'Merge Teammate\'s Note' : 'Select ICD-10 Codes to Keep'}
                </h3>
              </div>
              <Button variant="ghost" size="sm" onClick={() => { setShowMergeModal(false); setMergeText(''); setMergeStep('paste'); setMergeIcd10List([]); }} className="h-8 w-8 p-0">
                <span className="text-lg">&times;</span>
              </Button>
            </div>

            {mergeStep === 'paste' ? (
              <>
                <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                  <p className="text-sm text-muted-foreground">
                    Paste your teammate's WriteRight note below. You'll pick which ICD-10 codes to keep before merging.
                  </p>
                  <textarea
                    value={mergeText}
                    onChange={(e) => setMergeText(e.target.value)}
                    placeholder="Paste teammate's clinical note here..."
                    className="w-full h-64 p-3 rounded-lg border border-border bg-background text-sm font-mono resize-y focus:outline-none focus:ring-2 focus:ring-amber-500/50"
                    autoFocus
                    data-testid="merge-note-textarea"
                  />
                </div>
                <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => { setShowMergeModal(false); setMergeText(''); }}>Cancel</Button>
                  <Button
                    size="sm"
                    onClick={handleProceedToCodes}
                    disabled={!mergeText.trim()}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                    data-testid="merge-next-btn"
                  >
                    Next: Pick Codes
                    <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="p-4 space-y-4 flex-1 overflow-y-auto">
                  <p className="text-sm text-muted-foreground">
                    Check the codes you want in the merged note. Unchecked codes and their A/P items will be dropped.
                  </p>

                  {/* My codes */}
                  {mergeIcd10List.filter(c => c.source === 'mine').length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Your Codes</p>
                      <div className="space-y-1.5">
                        {mergeIcd10List.filter(c => c.source === 'mine').map(item => (
                          <label key={item.code} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${item.checked ? 'border-primary/40 bg-primary/5' : 'border-border/50 bg-muted/30 opacity-50'}`}>
                            <input
                              type="checkbox"
                              checked={item.checked}
                              onChange={() => toggleMergeCode(item.code)}
                              className="w-4 h-4 rounded accent-amber-500"
                            />
                            <span className="font-mono text-sm font-bold text-primary">{item.code}</span>
                            {item.description && <span className="text-xs text-muted-foreground">{item.description}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Teammate codes */}
                  {mergeIcd10List.filter(c => c.source === 'teammate').length > 0 && (
                    <div>
                      <p className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-2">Teammate's Codes</p>
                      <div className="space-y-1.5">
                        {mergeIcd10List.filter(c => c.source === 'teammate').map(item => (
                          <label key={item.code} className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${item.checked ? 'border-amber-500/40 bg-amber-500/5' : 'border-border/50 bg-muted/30 opacity-50'}`}>
                            <input
                              type="checkbox"
                              checked={item.checked}
                              onChange={() => toggleMergeCode(item.code)}
                              className="w-4 h-4 rounded accent-amber-500"
                            />
                            <span className="font-mono text-sm font-bold text-amber-400">{item.code}</span>
                            {item.description && <span className="text-xs text-muted-foreground">{item.description}</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground">
                    {mergeIcd10List.filter(c => c.checked).length} codes selected for merge
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2 p-4 border-t border-border">
                  <Button variant="outline" size="sm" onClick={() => setMergeStep('paste')}>
                    Back
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleMergeNote}
                    disabled={isMerging || mergeIcd10List.filter(c => c.checked).length === 0}
                    className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                    data-testid="merge-submit-btn"
                  >
                    {isMerging ? <><Loader2 className="w-4 h-4 animate-spin" />Merging...</> : <><GitMerge className="w-4 h-4" />Merge Notes</>}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OutputDocumentsPanel;
